import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const field = "w-full rounded-xl border border-[#F4EEE2]/15 bg-[#0B111E] px-4 py-3 text-[#F4EEE2] outline-none transition-colors focus:border-[#D4AF37]";
const label = "mt-6 block font-ui text-xs uppercase tracking-widest text-[#B7BFCC]";
const btn = "mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3.5 font-ui font-semibold text-[#0B111E] transition-colors hover:bg-[#F3C846] disabled:opacity-60";

const Card = ({ testid, title, lead, onSubmit, children }) => (
  <main data-testid={testid} className="grid min-h-screen place-items-center px-5">
    <form onSubmit={onSubmit} className="w-full max-w-md rounded-[28px] border border-[#D4AF37]/20 bg-[#131C2E] p-8 md:p-10">
      <img src="/images/logo-round.png" alt="Awa In Kelet logo" className="h-16 w-16 rounded-full ring-1 ring-[#D4AF37]/50" />
      <h1 className="font-display mt-6 text-4xl font-semibold text-[#F4EEE2]">{title}</h1>
      <p className="mt-2 text-sm text-[#B7BFCC]">{lead}</p>
      {children}
      <Link to="/admin/login" data-testid="back-to-login-link" className="mt-6 block text-center font-ui text-xs text-[#B7BFCC] hover:text-[#D4AF37]">← Back to sign in</Link>
    </form>
  </main>
);

const useSubmit = (fn) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await fn(); } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); } finally { setBusy(false); }
  };
  return { busy, error, submit, setError };
};

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, error, submit } = useSubmit(async () => { await api.post("/auth/forgot-password", { email }); setSent(true); });
  return (
    <Card testid="forgot-password-page" title="Forgot password?" lead="Type the email you sign in with. We'll email you a link to choose a new password." onSubmit={submit}>
      {sent ? (
        <p data-testid="forgot-password-sent" className="mt-8 flex gap-3 rounded-2xl border border-[#D4AF37]/30 p-4 text-sm text-[#F4EEE2]"><MailCheck className="h-5 w-5 shrink-0 text-[#D4AF37]" />If that email has a Team Portal account, a reset link is on its way. It works for 1 hour. Check your spam folder too.</p>
      ) : (
        <>
          <label className={label}>Email</label>
          <input data-testid="forgot-password-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${field} mt-2`} />
          {error && <p data-testid="forgot-password-error" className="mt-4 text-sm text-[#F2A08A]">{error}</p>}
          <button data-testid="forgot-password-submit-btn" disabled={busy} className={btn}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Email me a reset link</button>
        </>
      )}
    </Card>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const { busy, error, submit, setError } = useSubmit(async () => {
    if (pw !== pw2) return setError("The two passwords don't match.");
    await api.post("/auth/reset-password", { token, password: pw });
    setDone(true);
  });
  return (
    <Card testid="reset-password-page" title="New password" lead="Choose a new password with at least 8 characters." onSubmit={submit}>
      {done ? (
        <div data-testid="reset-password-done" className="mt-8 rounded-2xl border border-[#D4AF37]/30 p-4 text-sm text-[#F4EEE2]">
          Your password has been changed. <Link to="/admin/login" data-testid="reset-password-signin-link" className="font-semibold text-[#D4AF37] underline">Sign in now</Link>
        </div>
      ) : !token ? (
        <p data-testid="reset-password-no-token" className="mt-8 text-sm text-[#F2A08A]">This link is missing its code. Please use the link in your email, or <Link to="/admin/forgot-password" className="underline">ask for a new one</Link>.</p>
      ) : (
        <>
          <label className={label}>New password</label>
          <input data-testid="reset-password-input" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className={`${field} mt-2`} />
          <label className={label}>Type it again</label>
          <input data-testid="reset-password-confirm-input" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className={`${field} mt-2`} />
          {error && <p data-testid="reset-password-error" className="mt-4 text-sm text-[#F2A08A]">{error}</p>}
          <button data-testid="reset-password-submit-btn" disabled={busy} className={btn}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save new password</button>
        </>
      )}
    </Card>
  );
}
